import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import CommonService from "../../services/common/commonService";
import { TimelineItem, TimelineTooltipPayload, validateDate } from "../../utils/utils";
import "../../styles/account.css";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import EmailIcon from "../../assets/accounts/email-icon.svg";
import EmailIncomingIcon from "../../assets/accounts/email-incoming.svg";
import EmailOutgoingIcon from "../../assets/accounts/email-outgoing.svg";
import { EmailDirection } from "../../utils/types";

type EmailDetail = {
  id: string;
  start_time: string;
  from: string;
  to: string;
  subject: string;
  summary: string;
  direction?: EmailDirection;
};

const joinValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ");
  }
  return typeof value === "string" ? value : "";
};

const normalizeEmailItem = (
  item: any,
  fallback?: TimelineItem
): EmailDetail => ({
  id:
    item?.id ||
    fallback?.id ||
    "",
  start_time:
    item?.messageDate ||
    fallback?.start_time ||
    "",
  from: joinValue( item?.fromEmail),
  to: joinValue(item?.toEmail ),
  subject: item?.subject || "Email",
  summary:
    item?.body ||
    "",
  direction: normalizeDirection(item?.direction),
});

const normalizeDirection = (direction: unknown): EmailDirection | undefined => {
  const value = typeof direction === "string" ? direction.trim().toUpperCase() : "";
  if (value === EmailDirection.INBOUND) return EmailDirection.INBOUND;
  if (value === EmailDirection.OUTBOUND) return EmailDirection.OUTBOUND;
  return undefined;
};

const getEmailIcon = (direction?: EmailDirection) => {
  if (direction === EmailDirection.INBOUND) return EmailIncomingIcon;
  if (direction === EmailDirection.OUTBOUND) return EmailOutgoingIcon;
  return EmailIcon;
};

const getSummaryKey = (email: EmailDetail) =>
  email.id || `${email.start_time}-${email.subject}-${email.from}`;

const normalizeEmailResponse = (
  response: any,
  fallbackItems: TimelineItem[]
): EmailDetail[] => {
  const payload =
    response?.data ||
    response;

  const rawItems = Array.isArray(payload)
    ? payload
    : payload && typeof payload === "object"
      ? Object.values(payload)
      : [];

  if (!rawItems.length) {
    return fallbackItems.map((item) => normalizeEmailItem(item, item));
  }

  const fallbackMap = new Map(fallbackItems.map((item) => [item.id, item]));

  return rawItems.map((item: any, index) =>
    normalizeEmailItem(
      item,
      fallbackMap.get(item?.id) || fallbackItems[index]
    )
  );
};

const EmailTooltip = ({
  mode,
  interactions,
  emailIds = [],
  embedded = false,
}: TimelineTooltipPayload & { embedded?: boolean }) => {
  const [expandedSummaries, setExpandedSummaries] = useState<
    Record<string, boolean>
  >({});
  const fallbackEmails = useMemo(
    () => interactions.map((item: TimelineItem) => normalizeEmailItem(item, item)),
    [interactions]
  );
  const { showSnackbar } = useDisplaySnackbar();
  const {
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
    data,
  } = CommonService.useGetEmailSummary(
    { emailMessageIds: emailIds },
    emailIds.length > 0
  );
  const [emails, setEmails] = useState<EmailDetail[]>(fallbackEmails);

  useEffect(() => {
    setEmails(fallbackEmails);
  }, [fallbackEmails]);

  useEffect(() => {
    if (isSuccess && data) {
      setEmails(normalizeEmailResponse(data, interactions));
    }
    if (isError) {
      const errorMessage = error?.message || "Failed to load email summary.";
      showSnackbar("error", errorMessage);
    }
  }, [data, interactions, isSuccess, isError, error]);

  const sortedEmails = useMemo(
    () =>
      [...emails].sort(
        (a, b) =>
          new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      ),
    [emails]
  );
  const activeEmail = sortedEmails[0];
  const showSpinner = isLoading || isFetching;

  const toggleSummary = (emailId: string) => {
    setExpandedSummaries((current) => ({
      ...current,
      [emailId]: !current[emailId],
    }));
  };

  const renderSummary = (email: EmailDetail, className: string) => {
    const summary = email.summary || "No summary available.";
    const isExpandable = summary.length > 220;
    const summaryKey = getSummaryKey(email);
    const isExpanded = expandedSummaries[summaryKey] === true;

    return (
      <Box className="email-tooltip__summary-block">
        <Typography
          className={`${className} ${
            !isExpanded && isExpandable ? "email-tooltip__summary--clamped" : ""
          }`}
        >
          {summary}
        </Typography>
        {isExpandable && (
          <Typography
            component="button"
            type="button"
            className="email-tooltip__summary-toggle"
            onClick={() => toggleSummary(summaryKey)}
          >
            {isExpanded ? "Read less" : "Read more"}
          </Typography>
        )}
      </Box>
    );
  };

  const renderEmailMeta = (email: EmailDetail) => (
    <Box className="email-tooltip__meta">
      <Box className="email-tooltip__email-header">
        <img
          className="email-tooltip__email-icon"
          src={getEmailIcon(email.direction)}
          alt={
            email.direction === EmailDirection.OUTBOUND
              ? "Outbound email"
              : email.direction === EmailDirection.INBOUND
              ? "Inbound email"
              : "Email"
          }
        />
        <Typography
          className="email-tooltip__email-title"
          title={email?.subject}
        >
          {email?.subject || "Email"}
        </Typography>

        <Box className="tooltip__time">
          <AccessTimeIcon sx={{ fontSize: "14px" }} />
          {validateDate(email.start_time, "hh:mm A")}
        </Box>
      </Box>
      <Box className="email-tooltip__meta-row">
        <Box className="email-tooltip__meta-block">
          <Box display="flex" alignItems="center" gap="8px">
            <Typography className="email-tooltip__meta-label">FROM</Typography>
            <Box className="email-tooltip__header__line"/>
          </Box>
          <Typography className="email-tooltip__meta-value">
            {email?.from || "-"}
          </Typography>
        </Box>
      </Box>
      <Box className="email-tooltip__meta-row">
        <Box className="email-tooltip__meta-block">
          <Box display="flex" alignItems="center" gap="8px">
            <Typography className="email-tooltip__meta-label">TO</Typography>
            <Box className="email-tooltip__header__line"/>
          </Box>
          <Typography className="email-tooltip__meta-value">
            {email?.to || "-"}
          </Typography>
        </Box>
      </Box>
      <Box className="email-tooltip__meta-row email-tooltip__meta-row--subject">
        <Typography className="email-tooltip__meta-label">SUBJECT:</Typography>
        <Typography className="email-tooltip__subject">
          {email?.subject || "Email"}
        </Typography>
      </Box>
    </Box>
  );

  const emailList = (
    <Box className="email-tooltip__group-list">
      {sortedEmails.map((email, index) => (
          <Box key={email.id || `${email.start_time}-${index}`}>
          <Box className="email-tooltip__group-item">
            {renderEmailMeta(email)}
            {renderSummary(
              email,
              "email-tooltip__summary"
            )}
          </Box>
          {index !== sortedEmails.length - 1 && (
            <Divider className="email-tooltip__group-divider" />
          )}
        </Box>
      ))}
    </Box>
  );

  if (embedded) {
    return (
      <Box className="email-tooltip email-tooltip--embedded">
        {showSpinner && (
          <Box className="interaction-tooltip__spinner">
            <CircularProgress size={24} />
          </Box>
        )}
        {emailList}
      </Box>
    );
  }

  return (
    <Box className="interaction-tooltip email-tooltip">
      {showSpinner && (
        <Box className="interaction-tooltip__spinner">
          <CircularProgress size={24} />
        </Box>
      )}

      {mode === "group" ? (
        <>
          <Box className="interaction-tooltip__header">
            <Typography variant="h5">
              {sortedEmails.length} Emails
            </Typography>
            <Typography className="interaction-tooltip__date">
              {validateDate(sortedEmails[0]?.start_time, "DD MMM, YYYY")}
            </Typography>
          </Box>

          <Divider className="interaction-tooltip__divider" />

          {emailList}
        </>
      ) : (
        <>
          <Box className="interaction-tooltip__header">
            <Typography variant="h5">
              1 Email
            </Typography>
            <Typography className="interaction-tooltip__date">
              {validateDate(activeEmail?.start_time, "DD MMM, YYYY")}
            </Typography>
          </Box>

          <Divider className="interaction-tooltip__divider" />

          <Box className="email-tooltip__group-list">
            <Box className="email-tooltip__group-item">
              {activeEmail && renderEmailMeta(activeEmail)}
              {activeEmail && renderSummary(
                activeEmail,
                "email-tooltip__summary"
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default EmailTooltip;
